import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  SignOutRegular,
  SettingsRegular,
  QuestionCircleRegular,
} from '@fluentui/react-icons';
import { useAuth } from '@/hooks/useAuth';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginRight: tokens.spacingHorizontalS,
  },
  userName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  userEmail: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  menuHeader: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  menuUserInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  menuUserDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
});

export const UserProfile: React.FC = () => {
  const styles = useStyles();
  const { user, logout, isLoading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) {
    return null;
  }

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
  };

  const getUserInitials = () => {
    if (user.name) {
      const names = user.name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    return user.username?.[0]?.toUpperCase() || 'U';
  };

  return (
    <div className={styles.container}>
      <div className={styles.userInfo}>
        <Text className={styles.userName}>{user.name || user.username}</Text>
        <Text className={styles.userEmail}>{user.username}</Text>
      </div>

      <Menu open={menuOpen} onOpenChange={(_, data) => setMenuOpen(data.open)}>
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            icon={
              <Avatar
                name={user.name || user.username}
                initials={getUserInitials()}
                size={32}
                color="colorful"
              />
            }
          />
        </MenuTrigger>

        <MenuPopover>
          <div className={styles.menuHeader}>
            <div className={styles.menuUserInfo}>
              <Avatar
                name={user.name || user.username}
                initials={getUserInitials()}
                size={48}
                color="colorful"
              />
              <div className={styles.menuUserDetails}>
                <Text weight="semibold">{user.name || user.username}</Text>
                <Text size={200}>{user.username}</Text>
                {user.tenantId && (
                  <Text size={100} truncate>
                    Tenant: {user.tenantId}
                  </Text>
                )}
              </div>
            </div>
          </div>

          <MenuList>
            <MenuItem icon={<PersonRegular />} disabled>
              My Profile
            </MenuItem>
            <MenuItem icon={<SettingsRegular />} disabled>
              Settings
            </MenuItem>
            <MenuItem icon={<QuestionCircleRegular />} disabled>
              Help & Support
            </MenuItem>
            <MenuDivider />
            <MenuItem
              icon={<SignOutRegular />}
              onClick={handleLogout}
              disabled={isLoading}
            >
              Sign Out
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};